import { useEffect, useState } from "react";
import { LogOut } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import Logo, { LogoMark } from "../components/Logo";
import SlideEditor from "./SlideEditor";
import SharePanel from "./SharePanel";
import AnalyticsPanel from "./AnalyticsPanel";

const TABS = [
  { id: "slides", label: "Slide" },
  { id: "share", label: "Condivisione" },
  { id: "analytics", label: "Analytics" },
];

export default function AdminPage() {
  const [user, setUser] = useState(null); // null = checking, false = logged out
  const [tab, setTab] = useState("slides");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(false));
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      toast.success("Accesso effettuato");
    } catch (err) {
      setError(formatApiError(err, "Accesso non riuscito"));
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
  };

  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-mist">
        <div className="animate-pulse">
          <LogoMark size={40} />
        </div>
      </div>
    );
  }

  if (user === false) {
    return (
      <div className="min-h-screen bg-mist bg-blueprint-light flex items-center justify-center px-6" data-testid="admin-login">
        <div className="w-full max-w-sm bg-white border border-line rounded-2xl p-8 shadow-xl shadow-slate-200/50">
          <div className="flex flex-col items-center text-center">
            <LogoMark size={40} />
            <h1 className="mt-4 font-display font-extrabold tracking-tight text-2xl text-ink">Pannello Admin</h1>
            <p className="mt-1.5 text-sm text-slate-500">Accedi per modificare la presentazione.</p>
          </div>
          <form onSubmit={login} className="mt-7 space-y-3">
            <input
              data-testid="admin-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              className="w-full rounded-xl border border-line px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
            />
            <input
              data-testid="admin-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="w-full rounded-xl border border-line px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
            />
            {error && (
              <p data-testid="admin-login-error" className="text-sm text-brand">
                {error}
              </p>
            )}
            <button
              data-testid="admin-login-button"
              type="submit"
              disabled={loading}
              className="w-full rounded-xl bg-brand hover:bg-brand-hover text-white font-display font-semibold text-sm px-4 py-3 transition-colors disabled:opacity-40"
            >
              {loading ? "Accesso…" : "Accedi"}
            </button>
          </form>
          <a href="/" className="block mt-5 text-center font-mono text-[11px] uppercase tracking-widest text-slate-400 hover:text-ink transition-colors">
            ← Torna al deck
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-mist" data-testid="admin-panel">
      <header className="bg-white border-b border-line">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-4">
            <span className="hidden sm:block font-mono text-[11px] text-slate-400">{user.email}</span>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-brand transition-colors"
            >
              <LogOut size={15} />
              Esci
            </button>
          </div>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-6 py-8">
        <div className="inline-flex bg-white border border-line rounded-full p-1 gap-1 mb-8">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`px-5 py-2 rounded-full text-sm font-display font-semibold transition-colors ${
                tab === t.id ? "bg-ink text-white" : "text-slate-500 hover:text-ink"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        {tab === "slides" && <SlideEditor />}
        {tab === "share" && <SharePanel />}
        {tab === "analytics" && <AnalyticsPanel />}
      </main>
    </div>
  );
}
